/**
 * SecretInput - Masked password input with reveal toggle
 */
import { useState } from "react";
import { Eye, EyeOff } from "@untitledui/icons";
import { Group as AriaGroup, Input as AriaInput, TextField as AriaTextField } from "react-aria-components";
import { cx } from "@/utils/cx";

interface SecretInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isDisabled?: boolean;
    isInvalid?: boolean;
    hint?: string;
    "aria-label"?: string;
}

export function SecretInput({
    value,
    onChange,
    placeholder = "••••••••",
    isDisabled = false,
    isInvalid = false,
    hint,
    "aria-label": ariaLabel,
}: SecretInputProps) {
    const [isRevealed, setIsRevealed] = useState(false);

    // Check if value is masked (from API, e.g., "***...***")
    const isMasked = value.includes("***");

    return (
        <AriaTextField
            aria-label={ariaLabel}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            className="group flex w-full flex-col items-start justify-start gap-1.5"
        >
            <AriaGroup
                className={({ isFocusWithin, isDisabled, isInvalid }) =>
                    cx(
                        "relative flex w-full flex-row place-content-center place-items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset",
                        isFocusWithin && !isDisabled && "ring-2 ring-brand",
                        isDisabled && "cursor-not-allowed bg-disabled_subtle ring-disabled",
                        isInvalid && "ring-error_subtle",
                        isInvalid && isFocusWithin && "ring-2 ring-error",
                    )
                }
            >
                <AriaInput
                    type={isRevealed && !isMasked ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={cx(
                        "m-0 w-full bg-transparent px-3 py-2 pr-10 text-md text-primary ring-0 outline-hidden placeholder:text-placeholder",
                        isDisabled && "cursor-not-allowed text-disabled",
                        isMasked && "font-mono text-tertiary",
                    )}
                />

                {/* Reveal toggle button */}
                <button
                    type="button"
                    onClick={() => setIsRevealed(!isRevealed)}
                    disabled={isDisabled || isMasked}
                    className={cx(
                        "absolute right-3 text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover",
                        (isDisabled || isMasked) && "cursor-not-allowed opacity-50",
                    )}
                    aria-label={isRevealed ? "Hide value" : "Show value"}
                >
                    {isRevealed && !isMasked ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
            </AriaGroup>

            {hint && <p className={cx("text-sm", isInvalid ? "text-error-primary" : "text-tertiary")}>{hint}</p>}
        </AriaTextField>
    );
}
